import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAudio } from '@/contexts/AudioContext';
import { Sparkles } from 'lucide-react';

/**
 * "Welcome Back" interstitial for auto-logged-in returning users (mobile app
 * cold-start). The button tap is the user gesture that unlocks audio, then we
 * route into Modules where that page's background music begins.
 */
const WelcomeBack = ({ user }) => {
  const navigate = useNavigate();
  const { unlockAudio } = useAudio();

  const firstName =
    (user?.full_name && user.full_name.trim().split(' ')[0]) || user?.username || 'Seeker';

  const handleContinue = () => {
    try {
      unlockAudio && unlockAudio();
    } catch (e) {
      /* best-effort audio unlock */
    }
    sessionStorage.setItem('welcomeBackShown', '1');
    navigate('/dashboard/modules');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #1a1040 0%, #2a1a5e 50%, #0f1f3a 100%)' }}
      data-testid="welcome-back-page"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 50% 38%, rgba(34,211,238,0.18), transparent 60%)' }}
      />
      <div className="relative z-10 text-center px-6 max-w-xl">
        <div
          className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-8 border border-cyan-400/40"
          style={{ background: 'rgba(34,211,238,0.08)' }}
        >
          <Sparkles className="w-7 h-7 text-cyan-300" />
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
          data-testid="welcome-back-heading"
        >
          Welcome back, {firstName}
        </h1>
        <p
          className="text-lg text-cyan-100/80 mb-12 leading-relaxed"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Take a breath. Your path is exactly where you left it — return to presence
          and continue your journey within.
        </p>
        <Button
          onClick={handleContinue}
          data-testid="welcome-back-continue-btn"
          className="px-10 py-6 text-lg rounded-full bg-cyan-500 hover:bg-cyan-400 text-[#0f1f3a] font-semibold transition-all hover:scale-105 shadow-lg shadow-cyan-500/30"
        >
          Continue Your Journey
        </Button>
      </div>
    </div>
  );
};

export default WelcomeBack;
